import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { diffVersions, type LineChange } from '@muddle/core';
import { api } from '../api';
import { useAsync } from '../hooks/useAsync';
import { useTitle } from '../shortcuts';
import { ErrorMessage, PageHeader, Skeleton } from '../components';
import { formatDate, lineText } from '../format';

const CHANGE_LABEL: Record<LineChange, string> = {
  same: 'Same',
  changed: 'Changed',
  added: 'Added',
  removed: 'Removed',
};
const FIELD_LABEL = {
  technique: 'Technique',
  glass: 'Glass',
  ice: 'Ice',
  garnish: 'Garnish',
  methodNotes: 'Method notes',
} as const;

const signed = (n: number, digits = 1) =>
  `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toFixed(digits)}`;

export function ComparePage() {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, reload] = useAsync(() => api.getCocktail(id), [id]);
  const [settings] = useAsync(api.getSettings);
  useTitle(state.status === 'ready' ? `Compare · ${state.data.cocktail.name}` : 'Compare');

  if (state.status === 'loading') {
    return (
      <div className="page">
        <Skeleton height={200} />
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div className="page">
        <ErrorMessage message={`Couldn't load this drink. ${state.message}`} onRetry={reload} />
      </div>
    );
  }

  const { cocktail, versions } = state.data;
  if (versions.length < 2) {
    return (
      <div className="page">
        <PageHeader kicker={cocktail.name} title="Compare versions" />
        <div className="card empty">
          <div className="empty-title">Only one version so far</div>
          Log a tweak to create a second version, then compare them here.
          <div>
            <button
              className="btn btn-primary"
              type="button"
              onClick={() => navigate(`/drinks/${id}/edit`)}
            >
              Log tweak
            </button>
          </div>
        </div>
      </div>
    );
  }

  const byNumber = (n: string | null) => versions.find((v) => String(v.versionNumber) === n);
  // Default: the previous version against the newest.
  const a = byNumber(params.get('a')) ?? versions[1]!;
  const b = byNumber(params.get('b')) ?? versions[0]!;
  const unit = settings.status === 'ready' ? settings.data.defaultUnit : 'oz';
  const diff = diffVersions(a, b);
  const changedFields = diff.fields.filter((f) => f.changed);
  const changedCount = diff.lines.filter((l) => l.change !== 'same').length;

  const pick = (which: 'a' | 'b', value: string) => {
    const next = new URLSearchParams(params);
    next.set('a', which === 'a' ? value : String(a.versionNumber));
    next.set('b', which === 'b' ? value : String(b.versionNumber));
    setParams(next, { replace: true });
  };

  return (
    <div className="page mu-wide">
      <PageHeader
        kicker={cocktail.name}
        title="Compare versions"
        sub={
          a.id === b.id
            ? 'Pick two different versions.'
            : `${changedCount} ingredient change${changedCount === 1 ? '' : 's'}, ${changedFields.length} method change${changedFields.length === 1 ? '' : 's'}.`
        }
        actions={
          <Link className="btn btn-ghost" to={`/drinks/${id}`}>
            Back to drink
          </Link>
        }
      />

      <section className="card" aria-label="Choose versions">
        <div className="row row-2">
          {(
            [
              ['a', 'From', a],
              ['b', 'To', b],
            ] as const
          ).map(([which, label, v]) => (
            <div key={which}>
              <label htmlFor={`cmp-${which}`}>{label}</label>
              <select
                id={`cmp-${which}`}
                value={v.versionNumber}
                onChange={(e) => pick(which, e.target.value)}
              >
                {versions.map((o) => (
                  <option key={o.id} value={o.versionNumber}>
                    v{o.versionNumber} · {formatDate(o.createdAt)}
                  </option>
                ))}
              </select>
              <div className="hint">
                {v.changeNote || (v.versionNumber === 1 ? 'First version' : 'No change note')}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="card" aria-labelledby="cmp-ing">
        <h2 className="card-title" id="cmp-ing">
          Ingredients
        </h2>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Ingredient</th>
                <th className="num">v{a.versionNumber}</th>
                <th className="num">v{b.versionNumber}</th>
                <th>Change</th>
              </tr>
            </thead>
            <tbody>
              {diff.lines.map((l, i) => (
                <tr key={`${l.ingredientId}-${i}`} className={`mu-diff mu-diff-${l.change}`}>
                  <td>{l.ingredientName}</td>
                  <td className="num">{l.a ? lineText(l.a, unit) : '—'}</td>
                  <td className="num">{l.b ? lineText(l.b, unit) : '—'}</td>
                  <td>
                    <span className="mu-diff-tag">{CHANGE_LABEL[l.change]}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card" aria-labelledby="cmp-more">
        <h2 className="card-title" id="cmp-more">
          Method and strength
        </h2>
        {changedFields.length === 0 && (
          <p className="muted">Technique, glass, ice, garnish and notes are the same.</p>
        )}
        {changedFields.map((f) => (
          <div key={f.field} className="list-row mu-diff mu-diff-changed">
            <span className="mu-diff-field">{FIELD_LABEL[f.field]}</span>
            <span className="grow">
              {f.a || '—'} <span aria-label="changed to">→</span> {f.b || '—'}
            </span>
          </div>
        ))}
        <div className="list-row">
          <span className="mu-diff-field">ABV (est.)</span>
          <span className="grow num">
            {a.abvEst === null ? '—' : `${a.abvEst.toFixed(1)}%`} →{' '}
            {b.abvEst === null ? '—' : `${b.abvEst.toFixed(1)}%`}
            {diff.abvDelta !== null &&
              Math.abs(diff.abvDelta) >= 0.05 &&
              ` (${signed(diff.abvDelta)} pts)`}
          </span>
        </div>
        <div className="list-row">
          <span className="mu-diff-field">Volume (est.)</span>
          <span className="grow num">
            {a.volumeEst === null ? '—' : `${Math.round(a.volumeEst)} ml`} →{' '}
            {b.volumeEst === null ? '—' : `${Math.round(b.volumeEst)} ml`}
            {diff.volumeDelta !== null &&
              Math.abs(diff.volumeDelta) >= 0.5 &&
              ` (${signed(diff.volumeDelta, 0)} ml)`}
          </span>
        </div>
      </section>
    </div>
  );
}
